// CommonMark: elk ASCII-leesteken mag ge-escaped worden met een backslash.
const ESCAPE_PATTERN = /\\([\]\\!"#$%&'()*+,./:;<=>?@[^_`{|}~-])/g;
// Unicode Private Use Area-teken: onwaarschijnlijk in brontekst, in
// tegenstelling tot een gewone spatie die zou botsen met cijferreeksen als
// "06 12 54".
const PLACEHOLDER = '';

function isSeparatorCell(cell: string): boolean {
  return /^:?-+:?$/.test(cell.trim());
}

function splitTableCells(line: string): string[] {
  let trimmed = line.trim();
  if (trimmed.startsWith('|')) trimmed = trimmed.slice(1);
  if (trimmed.endsWith('|')) trimmed = trimmed.slice(0, -1);
  return trimmed.split(/(?<!\\)\|/).map((cell) => cell.trim());
}

function stripBlockPrefix(line: string): string {
  return line
    .replace(/^\s{0,3}#{1,6}\s+/, '')
    .replace(/^\s*(?:[-*+]|\d+[.)])\s+/, '');
}

/**
 * Verwijdert inline Markdown-opmaak (links, vet, cursief, escapes) uit een
 * enkele regel/cel. Escapes worden eerst achter een placeholder verstopt,
 * anders leest een ge-escapet leesteken als een emphasis-marker en strippen
 * de vet/cursief-regexes het alsnog.
 */
function stripInlineMarkdown(text: string): string {
  const escaped: string[] = [];
  const protectedText = text.replace(ESCAPE_PATTERN, (_match, ch: string) => {
    escaped.push(ch);
    return `${PLACEHOLDER}${escaped.length - 1}${PLACEHOLDER}`;
  });

  const stripped = protectedText
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/(\*\*\*|___)([^\n]+?)\1/g, '$2')
    .replace(/(\*\*|__)([^\n]+?)\1/g, '$2')
    .replace(/\*([^*\n]+)\*/g, '$1')
    .replace(/_([^_\n]+)_/g, '$1');

  return stripped.replace(
    new RegExp(`${PLACEHOLDER}(\\d+)${PLACEHOLDER}`, 'g'),
    (_match, index: string) => escaped[Number(index)] ?? '',
  );
}

/**
 * Zet een (mogelijk Markdown-opgemaakte) extractiefragment om naar platte
 * tekst die letterlijk in het brondocument terug te vinden is. Tabelrijen
 * worden ontleed tot hun losse celwaarden (lege cellen en scheidingsrijen
 * vervallen); overige regels worden ontdaan van kopjes, opsommingstekens,
 * vet/cursief, links en escape-tekens. Regeleinden worden woordscheidingen,
 * zoals ze ook door de zoeklogica al tolerant behandeld worden.
 */
export function stripMarkdownToPlainText(text: string): string {
  const lines = text.split('\n').map((line) => {
    if (line.includes('|')) {
      const cells = splitTableCells(line);
      if (cells.every(isSeparatorCell)) return '';
      return cells
        .filter((cell) => cell.length > 0)
        .map(stripInlineMarkdown)
        .join(' ');
    }
    return stripInlineMarkdown(stripBlockPrefix(line));
  });

  return lines.join(' ').replace(/\s+/g, ' ').trim();
}

/**
 * Bouwt de geordende lijst kandidaat-teksten om een evidence-fragment in een
 * document te lokaliseren: eerst de door de backend meegeleverde platte
 * tekst, dan de client-side gestripte variant van de (mogelijk Markdown-)
 * sentence als terugval voor bestaande data of wanneer de backend-tekst geen
 * match oplevert.
 */
export function buildHighlightCandidates(
  sentence?: string,
  plainSentence?: string,
): string[] {
  const candidates: string[] = [];
  const plain = plainSentence?.trim();
  if (plain) candidates.push(plain);

  const stripped = sentence ? stripMarkdownToPlainText(sentence).trim() : '';
  if (stripped && !candidates.includes(stripped)) candidates.push(stripped);

  return candidates;
}
